import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { PromoBanner } from '@/models';
import { ui } from '@/constants';
import { getDualPromoBannerInteraction } from '@/services/analytics/platforms/adobe-analytics/helpers';
import { HomePageLoadEvent } from '@/services/analytics/events';

interface ColorStyle {
  color?: string;
}

@Component({
  name: 'CmgDualPromoBanner',
})
export default class CmgDualPromoBanner extends Vue {
  @Prop({ type: Object })
  promoBanner?: PromoBanner;

  @Prop({ type: Boolean })
  public isLoggedIn!: boolean;

  get showPromoBanner(): boolean {
    return this.promoBanner ? this.promoBanner.showPromoBanner : false;
  }

  get showSecondaryPromo(): boolean {
    return this.promoBanner ? this.promoBanner.dualPromo : false;
  }

  get isDesktop(): boolean {
    return this.$appStore.state.ui.clientWidth > ui.breakpoints.tabletMax;
  }

  get backgroundImageUrl(): string | undefined {
    return this.promoBanner ? this.promoBanner.primary.backgroundImageUrl : undefined;
  }

  get title(): string | undefined {
    return this.promoBanner && this.promoBanner.primary.title;
  }

  get titleColor(): ColorStyle {
    return this.promoBanner && this.promoBanner.primary.titleColor ? { color: this.promoBanner.primary.titleColor } : {};
  }

  get subtitle(): string | undefined {
    return this.promoBanner ? this.promoBanner.primary.subtitle : undefined;
  }

  get subtitleColor(): ColorStyle {
    return this.promoBanner && this.promoBanner.primary.subtitleColor ? { color: this.promoBanner.primary.subtitleColor } : {};
  }

  get legal(): string | undefined {
    return this.promoBanner ? this.promoBanner.primary.legalText : undefined;
  }

  get legalColor(): ColorStyle {
    return this.promoBanner && this.promoBanner.primary.legalTextColor ? { color: this.promoBanner.primary.legalTextColor } : {};
  }

  get secondaryLegal(): string | undefined {
    return this.promoBanner && this.promoBanner.secondary ? this.promoBanner.secondary.legalText : undefined;
  }

  get secondaryLegalColor(): ColorStyle {
    return this.promoBanner && this.promoBanner.secondary && this.promoBanner.secondary.legalTextColor ? { color: this.promoBanner.secondary.legalTextColor } : {};
  }

  get image(): string | undefined {
    return this.isDesktop
      ? this.promoBanner?.primary?.imageUrlDesktop
      : this.promoBanner?.primary?.imageUrlMobile;
  }

  get video(): string | undefined {
    return this.isDesktop
      ? this.promoBanner?.primary?.videoUrlDesktop
      : this.promoBanner?.primary?.videoUrlMobile;
  }

  get shouldLoopVideo(): boolean | undefined {
    return this.promoBanner?.primary?.shouldLoopBottomVideo;
  }

  get navigationLink(): string | undefined {
    return this.promoBanner ? this.promoBanner.primary.buttonLink : undefined;
  }

  get buttonText(): string | undefined {
    return this.promoBanner ? this.promoBanner.primary.buttonText : undefined;
  }

  get buttonColor(): object {
    return this.promoBanner && this.promoBanner.primary.buttonColor ? { border: 0, backgroundColor: this.promoBanner.primary.buttonColor } : {};
  }

  get showButton(): boolean {
    return !!this.buttonText;
  }

  get secondaryTitle(): string | undefined {
    return this.promoBanner && this.promoBanner.secondary ? this.promoBanner.secondary.title : undefined;
  }

  get secondaryTitleColor(): ColorStyle {
    if (!this.promoBanner || !this.promoBanner.secondary) {
      return {};
    }
    return this.promoBanner.secondary.titleColor ? { color: this.promoBanner.secondary.titleColor } : {};
  }

  get secondarySubtitle(): string | undefined {
    return this.promoBanner && this.promoBanner.secondary ? this.promoBanner.secondary.subtitle : undefined;
  }

  get secondarySubtitleColor(): ColorStyle {
    if (!this.promoBanner || !this.promoBanner.secondary) {
      return {};
    }
    return this.promoBanner.secondary.subtitleColor ? { color: this.promoBanner.secondary.subtitleColor } : {};
  }

  get secondaryShowButton(): boolean {
    return this.promoBanner && this.promoBanner.secondary ? !!this.promoBanner.secondary.buttonText : false;
  }

  get secondaryNavigationLink(): string | undefined {
    return this.promoBanner && this.promoBanner.secondary ? this.promoBanner.secondary.buttonLink : undefined;
  }

  get secondaryButtonText(): string | undefined {
    return this.promoBanner && this.promoBanner.secondary ? this.promoBanner.secondary.buttonText : undefined;
  }

  get secondaryButtonColor(): object {
    if (!this.promoBanner || !this.promoBanner.secondary) {
      return {};
    }
    return this.promoBanner.secondary.buttonColor ? { border: 0, backgroundColor: this.promoBanner.secondary.buttonColor } : {};
  }

  get secondaryImage(): string | undefined {
    return this.isDesktop
      ? this.promoBanner?.secondary?.imageUrlDesktop
      : this.promoBanner?.secondary?.imageUrlMobile;
  }

  get secondaryVideo(): string | undefined {
    return this.isDesktop
      ? this.promoBanner?.secondary?.videoUrlDesktop
      : this.promoBanner?.secondary?.videoUrlMobile;
  }

  get shouldLoopSecondaryVideo(): boolean  | undefined {
    return this.promoBanner?.secondary?.shouldLoopBottomVideo;
  }

  navigateTo(section: string, sectionPosition: number): void {
    // Let's explicitly send the section / link position to the navigate call. Determining the position
    // based on the URL doesn't work properly if both cards in the dual promo banner navigate to the same
    // URL, in which case matching on the URL will always return section position 1
    const interaction = getDualPromoBannerInteraction(this.promoBanner, sectionPosition);
    this.$services.analytics.addLinkClickedEventToStore(interaction, section);
    const isNavigatingToSameLocation = section === location.hash;
    location.href = section || '';

    // To cover the edge case where both dual promo banners point to the home page top level menu, we need to manually
    // trigger a home page load event in order to send the associated link click tracking data to Adobe
    if (isNavigatingToSameLocation) {
      this.$services.analytics.send(new HomePageLoadEvent());
    }
  }
}
